/**
 * User Goal Validation Schemas
 *
 * Zod schemas for validating user goal-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Goal type, metric, and direction validation using select options
 * - Target value and date validation
 * - Optional starting value and distance fields
 * - Exercise GUID validation for exercise-specific goals
 * - Input/Output type exports
 * - Validation helper functions (validate, safeValidate, isValid)
 */

import { dateWithMinSchema } from './date.schemas';
import { z } from './zod';
import { dayjs } from '../utils/date';
import {
  getUserGoalTypeOptions,
  getUserGoalMetricOptions,
  getUserGoalDirectionOptions,
  createEnumFromOptions,
} from '../utils/select-options';

// =============================================================================
// COMMON VALIDATION RULES
// =============================================================================

/**
 * Type validation
 */
const typeSchema = createEnumFromOptions(getUserGoalTypeOptions());

/**
 * Metric validation
 */
const metricSchema = createEnumFromOptions(getUserGoalMetricOptions());

/**
 * Direction validation
 */
const directionSchema = createEnumFromOptions(getUserGoalDirectionOptions());

// =============================================================================
// USER GOAL VALIDATION SCHEMAS
// =============================================================================

/**
 * User goal form validation schema
 */
export const userGoalSchema = z.object({
  title: z
    .string()
    .min(1, 'Give the goal a name')
    .max(255, 'Name must be 255 characters or fewer'),
  description: z
    .string()
    .max(530, 'Description must be 530 characters or fewer')
    .optional(),
  type: typeSchema,
  target_metric: metricSchema,
  distance: z.number().min(0, 'Distance must be a positive number').optional(),
  starting_value: z
    .number()
    .min(0, 'Starting value must be a positive number')
    .optional(),
  target_value: z.number().min(0, 'Target value must be a positive number'),
  // Read per validation: a bound fixed at load would be a day behind after midnight.
  target_date: dateWithMinSchema(() => dayjs().add(1, 'day')),
  goal_direction: directionSchema,
  exercise_guid: z.string().min(1, 'Exercise GUID is required').optional(),
});

/**
 * Partial user goal schema for updates
 * All fields are optional for PATCH requests
 */
export const updateUserGoalSchema = userGoalSchema.partial();

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type UserGoalInput = z.input<typeof userGoalSchema>;
export type UserGoalOutput = z.output<typeof userGoalSchema>;
export type UpdateUserGoalInput = z.input<typeof updateUserGoalSchema>;
export type UpdateUserGoalOutput = z.output<typeof updateUserGoalSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate user goal data
 * @param data - The data to validate
 * @returns The validated and typed user goal data
 * @throws ZodError if validation fails
 */
export function validateUserGoal(data: unknown): UserGoalOutput {
  return userGoalSchema.parse(data);
}

/**
 * Safely validate user goal data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUserGoal(data: unknown) {
  return userGoalSchema.safeParse(data);
}

/**
 * Check if data is valid user goal data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUserGoal(data: unknown): boolean {
  return userGoalSchema.safeParse(data).success;
}

/**
 * Validate user goal update data
 * @param data - The data to validate
 * @returns The validated and typed user goal update data
 * @throws ZodError if validation fails
 */
export function validateUpdateUserGoal(data: unknown): UpdateUserGoalOutput {
  return updateUserGoalSchema.parse(data);
}

/**
 * Safely validate user goal update data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateUpdateUserGoal(data: unknown) {
  return updateUserGoalSchema.safeParse(data);
}

/**
 * Check if data is valid user goal update data
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidUpdateUserGoal(data: unknown): boolean {
  return updateUserGoalSchema.safeParse(data).success;
}
